import { AxiosInstance } from "axios";
import { Categories } from "../../types/categories";
import { ControlType } from "../../types/controlsRisk";
import { RiskDetailsDto, RiskItemDetailsDto } from "../../types/riskDetails";
import { Tag } from "../../types/tag";

const mapRiskItemDetailsDto = (item: RiskItemDetailsDto): ControlType => ({
  id: item.id,
  name: item.name,
  plannedStartDate: item.plannedStartDate,
  actualStartDate: item.actualStartDate ?? undefined,
  plannedFinishDate: item.plannedFinishDate,
  actualFinishDate: item.actualFinishDate ?? undefined,
  progressPerc: item.progressPerc,
  currentEvolution: item.currentEvolution
    ? {
        id: item.currentEvolution.id,
        description: item.currentEvolution.description,
        date: item.currentEvolution.date,
      }
    : undefined,
  status: item.status,
  globalGoal: item.globalGoal,
});

const mapRiskDetailsDto = (risk: RiskDetailsDto): Categories => ({
  id: risk.id,
  name: risk.name,
  category: risk.category,
  subcategory: risk.subcategory,
  theme: risk.theme,
  year: risk.year,
  severity: risk.severity,
  probability: risk.probability,
  priority: risk.priority,
  owner: risk.owner,
  managers: risk.managers,
  description: risk.description,
  finishedPerc: risk.finishedPerc,
  active: risk.active,
  items: risk.items.map(mapRiskItemDetailsDto),
  tags: risk.riskTags,
});

export const getRiskThemes = async (
  axiosInstance: AxiosInstance,
  year: number,
  riskPriority?: string[],
  isGlobalGoal?: boolean | null
) => {
  let queryParams = "";

  if (riskPriority && riskPriority.length > 0 && riskPriority[0] !== "") {
    queryParams = riskPriority
      .map(priority => `priority=${priority}`)
      .join("&");
  }

  if (isGlobalGoal !== null && isGlobalGoal !== undefined) {
    queryParams += queryParams ? "&" : "";
    queryParams += `isGlobalGoal=${isGlobalGoal}`;
  }

  return await axiosInstance
    .get(`/rest/risk/v1/list/year/${year}?${queryParams}`)
    .then(response => response.data);
};

export const getControlHistorys = async (
  axiosInstance: AxiosInstance,
  control_id: number
) => {
  return await axiosInstance
    .put(`/rest/risk-item-evolution/v1/list/${control_id}`)
    .then(response => response.data);
};

export const getAllYears = async (axiosInstance: AxiosInstance) => {
  return await axiosInstance
    .get(`/rest/risk/v1/years`)
    .then(response => response.data);
};

export const getRiskTheme = async (
  axiosInstance: AxiosInstance,
  risk_theme_id: string | undefined
) => {
  return await axiosInstance
    .get<RiskDetailsDto>(`/rest/risk/v1/${risk_theme_id}`)
    .then(response => mapRiskDetailsDto(response.data));
};

export const getCategories = async (
  axiosInstance: AxiosInstance,
  riskId?: number
) => {
  return await axiosInstance
    .get(`/rest/risk/v1/categories`, {
      params: riskId ? { riskId } : undefined,
    })
    .then(response => response.data);
};

export const getSubcategories = async (
  axiosInstance: AxiosInstance,
  riskId?: number
) => {
  return await axiosInstance
    .get(`/rest/risk/v1/subcategories`, {
      params: riskId ? { riskId } : undefined,
    })
    .then(response => response.data);
};

export const getAllCollaborators = async (axiosInstance: AxiosInstance) => {
  return await axiosInstance
    .get(`/rest/collaborator/v1/list`)
    .then(response => response.data);
};

export const getCurrentUserTags = async (axiosInstance: AxiosInstance) => {
  return await axiosInstance
    .get<{viewableTags: Tag[], adminTags: Tag[]}>(`/rest/collaborator/v1/logged/tags`)
    .then(response => response.data);
}

export const getInsights = async (
  axiosInstance: AxiosInstance,
  riskId: number,
  year: number = new Date().getFullYear(),
  month?: number | string
) => {
  return await axiosInstance
    .get(`/rest/risk-update/${riskId}`, {
      params: {
        year,
        month,
      },
    })
    .then(response => response.data);
};

export const getEvolutionChart = async (
  axiosInstance: AxiosInstance,
  year: number,
  priority?: string[],
  okr?: string[]
) => {
  let queryParams = "";

  if (priority && priority.length > 0 && priority[0] !== "") {
    queryParams = priority.map(priority => `priority=${priority}`).join("&");
  }

  if (okr && okr.length == 1) {
    queryParams += queryParams ? "&" : "";
    queryParams += okr.map(value => `goal=${value}`).join("&");
  }

  return await axiosInstance
    .get(`/rest/risk/v1/evolutionchart/${year}?${queryParams}`)
    .then(response => response.data);
};

export const getIsAdmin = async (axiosInstance: AxiosInstance) => {
  return await axiosInstance
    .get(`/rest/collaborator/v1/isAdmin`)
    .then(response => response.data);
};

export const getTenants = async (axiosInstance: AxiosInstance) => {
  return await axiosInstance
    .get(`/rest/collaborator/v1/tenants`)
    .then(response => response.data);
};

export const getTags = async (
  axiosInstance: AxiosInstance,
  params: { page?: number; size?: number; search?: string } = {}
) => {
  const page = params.page ?? 0;
  const size = params.size ?? 20;
  const search = params.search?.trim();

  const query = new URLSearchParams({
    page: String(page),
    size: String(size),
  });

  if (search) {
    query.set("search", search);
  }

  return await axiosInstance
    .get(`/rest/tags?${query.toString()}`)
    .then(response => response.data);
};
